import { AccessProfileService as GeneratedAccessProfileService } from '@wealthlane/api-services/wealthlane-customer-service';
import { Wealthlane_CustomerService_Dtos_AccessProfiles_AccessProfileType } from '@wealthlane/api-services/wealthlane-customer-service';
import {
  type AccessProfileScope,
  type AccessProfileDto,
  type AccessProfileListResponse,
  type AccessProfileQueryParams,
  type CreateAccessProfileDto,
  type UpdateAccessProfileDto,
  type DeleteAccessProfileResponseDto,
} from './types';

const AccessProfileTypeEnum = Wealthlane_CustomerService_Dtos_AccessProfiles_AccessProfileType;

export function scopeToType(scope: AccessProfileScope): Wealthlane_CustomerService_Dtos_AccessProfiles_AccessProfileType {
  return scope === 'FeatureSet' ? AccessProfileTypeEnum._1 : AccessProfileTypeEnum._2;
}

function mapToAccessProfileDto(raw: any, scope: AccessProfileScope): AccessProfileDto {
  return {
    id: raw.id ?? '',
    name: raw.name ?? '',
    description: raw.description ?? '',
    type: scope,
    isEnabled: raw.isEnabled ?? undefined,
    isEditable: raw.isEditable ?? undefined,
    isDefault: raw.isDefault ?? undefined,
    isDefaultAdminRole: raw.isDefaultAdminRole ?? undefined,
    permissions: Array.isArray(raw.permissions) ? raw.permissions : [],
    permissionChangeLogs: Array.isArray(raw.permissionChangeLogs) ? raw.permissionChangeLogs : null,
    linkedCount: raw.linkedCount ?? undefined,
    companyId: raw.companyId ?? undefined,
    createdAt: raw.creationTime ?? undefined,
    updatedAt: raw.lastModificationTime ?? undefined,
  };
}

function mapToListItem(raw: any, scope: AccessProfileScope) {
  const name = raw.name ?? '';
  return {
    ...raw,
    id: raw.id ?? '',
    name,
    roleName: name,
    description: raw.description ?? '',
    type: scope,
    isEnabled: raw.isEnabled ?? undefined,
    isEditable: raw.isEditable ?? undefined,
    isDefault: raw.isDefault ?? undefined,
    isDefaultAdminRole: raw.isDefaultAdminRole ?? undefined,
    linkedCount: raw.linkedCount ?? undefined,
    numberOfUsers: raw.linkedCount ?? 0,
    creationTime: raw.creationTime ?? undefined,
  };
}

export const AccessProfileFacade = {
  async getList(
    scope: AccessProfileScope,
    params: AccessProfileQueryParams,
    handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>
  ): Promise<AccessProfileListResponse | undefined> {
    const type = scopeToType(scope);
    const result = await handleRequest(
      GeneratedAccessProfileService.getApiCustomerApiAccessProfiles(
        type,
        params.filter,
        params.selectedPermissions,
        undefined,
        params.sorting,
        params.skipCount,
        params.maxResultCount
      )
    );
    if (!result) return undefined;
    return {
      items: (result.items ?? []).map((item) => mapToListItem(item, scope)),
      totalCount: result.totalCount ?? 0,
    };
  },

  async getById(
    scope: AccessProfileScope,
    id: string,
    handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>
  ): Promise<AccessProfileDto | undefined> {
    const type = scopeToType(scope);
    const result = await handleRequest(
      GeneratedAccessProfileService.getApiCustomerApiAccessProfiles1(id, type)
    );
    if (!result) return undefined;
    return mapToAccessProfileDto(result, scope);
  },

  async getPermissions(
    scope: AccessProfileScope,
    id: string,
    handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>
  ): Promise<any> {
    const type = scopeToType(scope);
    return await handleRequest(
      GeneratedAccessProfileService.getApiCustomerApiAccessProfilesPermissions(id, type)
    );
  },

  async create(
    scope: AccessProfileScope,
    data: CreateAccessProfileDto,
    handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>
  ): Promise<any> {
    const type = scopeToType(scope);
    return await handleRequest(
      GeneratedAccessProfileService.postApiCustomerApiAccessProfiles({
        type,
        name: scope === 'FeatureSet' ? data.name : undefined,
        roleName: scope === 'Role' ? data.name : undefined,
        description: data.description,
        permissions: data.permissions,
        fieldLevelPermissions: data.fieldLevelPermissions ?? [],
        isEnabled: data.isEnabled ?? true,
      })
    );
  },

  async update(
    scope: AccessProfileScope,
    data: UpdateAccessProfileDto,
    handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>
  ): Promise<any> {
    const type = scopeToType(scope);
    return await handleRequest(
      GeneratedAccessProfileService.putApiCustomerApiAccessProfiles(data.id, {
        type,
        name: scope === 'FeatureSet' ? data.name : undefined,
        roleName: scope === 'Role' ? data.name : undefined,
        description: data.description,
        permissions: data.permissions,
        fieldLevelPermissions: data.fieldLevelPermissions ?? [],
        isEnabled: data.isEnabled ?? true,
      })
    );
  },

  async deletePreview(
    scope: AccessProfileScope,
    id: string,
    handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>
  ): Promise<DeleteAccessProfileResponseDto | undefined> {
    const type = scopeToType(scope);
    const result = await handleRequest(
      GeneratedAccessProfileService.deleteApiCustomerApiAccessProfiles(id, type, false)
    );
    if (!result) return undefined;
    return {
      success: result.success ?? false,
      message: result.message ?? `Are you sure you want to delete this ${scope === 'FeatureSet' ? 'Feature Set' : 'System Role'}?`,
      isAssigned: result.isAssigned ?? false,
    };
  },

  async deleteConfirm(
    scope: AccessProfileScope,
    id: string,
    handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>
  ): Promise<any> {
    const type = scopeToType(scope);
    return await handleRequest(
      GeneratedAccessProfileService.deleteApiCustomerApiAccessProfiles(id, type, true)
    );
  },
};
